<template>
  <div class="introduction-wrapper">
    <div id="header-text">
      <span id="microphone" class="pi pi-microphone"></span>
      <h1 v-if="intro?.title">{{ intro.title }}</h1>
      <h2 v-if="intro?.subtitle" v-html="intro.subtitle"></h2>
    </div>

    <div class="intro-descr" v-if="intro">
      <h3 v-if="intro?.intro">{{ intro.intro }}</h3>
      <p v-if="intro?.description" v-html="intro.description"></p>
    </div>

    <div class="chapters-list" v-if="intro.generateSections">
      <div v-for="(item, index) in menuItems" :key="index" class="chapter-section" v-reveal>
        <div class="glowing-card">
          <div class="chapter-header">
            <span class="chapter-number">0{{ index + 1 }}</span>
            <h2 class="card-title">{{ item.title }}</h2>
          </div>

          <div class="card-content">
            <div class="image-container" v-if="item.image">
              <div v-if="item.useVideo" class="video-responsive">
                <iframe :src="item.image" frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen loading="lazy">
                </iframe>
              </div>
              <img v-else :src="item.image" :alt="item.title" loading="lazy" />
            </div>

            <div class="text-container">
              <div class="description-text" v-html="item.description"></div>

              <a :href="item.mapLink" target="_blank" v-if="item.mapLink" class="map-button">
                <span class="pi pi-map-marker"></span> {{ $t('introduction.showOnMap') }}
              </a>
            </div>
          </div>
        </div>

        <div class="chapter-divider" v-if="index < menuItems.length - 1"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TheIntroduction",
  props: {
    intro: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    menuItems() {
      return this.intro?.sections || [];
    },
  },
};
</script>

<style scoped>
.introduction-wrapper {
  padding-bottom: 100px;
}

/* Hlavní hlavička - centrovaná */
#header-text {
  display: block;
  text-align: center;
  padding-top: 50px;
  margin-bottom: 30px;
}

#microphone {
  font-size: 50px !important;
  color: var(--accent-primary);
  margin-bottom: 15px;
  text-shadow: 0 0 15px rgba(255, 152, 0, 0.4);
}

#header-text h1 {
  color: var(--text-primary);
  font-size: 48px;
  font-weight: 700;
  letter-spacing: -1.5px;
  margin-top: 0;
  margin-bottom: 15px;
  text-align: center;
}

#header-text h2 {
  color: var(--text-secondary);
  font-size: 17px;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  text-align: center;
}

/* Úvodní blok - nadpis centrovaný */
.intro-descr {
  max-width: 1100px;
  margin: 0 auto 80px auto;
  padding: 50px;
  background: var(--bg-panel);
  border-radius: 16px;
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-shadow);
  backdrop-filter: blur(6px);
}

.intro-descr h3 {
  color: var(--text-primary);
  font-size: 28px;
  margin-bottom: 30px;
  text-align: center;
}

.intro-descr p {
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.8;
  column-count: 2;
  column-gap: 60px;
  text-align: justify;
}

/* Kapitoly */
.chapters-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
}

.chapter-section {
  width: 100%;
  max-width: 1100px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.glowing-card {
  width: 100%;
  background: var(--bg-panel);
  backdrop-filter: blur(6px);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  padding: 50px;
  box-shadow: var(--glass-shadow);
  transition: border-color 0.4s ease, box-shadow 0.4s ease;
  transform: translateZ(0);
  will-change: transform, box-shadow;
}

.glowing-card:hover {
  border-color: var(--accent-neon);
  box-shadow: 0 0 25px rgba(0, 240, 255, 0.25);
}

.chapter-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
  text-align: center;
}

.chapter-number {
  font-family: var(--font-mono);
  color: var(--accent-primary);
  font-size: 24px;
  font-weight: 700;
  opacity: 0.6;
}

.card-title {
  color: var(--text-primary);
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  text-align: center;
}

.card-content {
  display: flex;
  gap: 60px;
  align-items: flex-start;
}

/* Média */
.image-container {
  flex: 0 0 50%;
  max-width: 50%;
}

.image-container img {
  width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  border: 1px solid var(--glass-border);
}

.video-responsive {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  border: 1px solid var(--glass-border);
  background: #000;
}

.video-responsive iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

/* Text */
.text-container {
  flex: 1;
}

.description-text {
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.8;
}

.map-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 35px;
  padding: 14px 28px;
  background: transparent;
  color: var(--accent-primary);
  border: 1px solid var(--accent-primary);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 13px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.map-button:hover {
  background: var(--accent-primary);
  color: var(--bg-primary);
  box-shadow: 0 0 20px rgba(255, 152, 0, 0.4);
  transform: translateY(-3px);
}

/* Oddělovač */
.chapter-divider {
  width: 2px;
  height: 60px;
  background: linear-gradient(to bottom, var(--accent-primary), transparent);
  margin-top: 60px;
  opacity: 0.3;
}

/* Responzivita */
@media only screen and (max-width: 900px) {
  #header-text h1 {
    font-size: 36px;
  }

  .intro-descr {
    padding: 30px;
    margin-bottom: 50px;
  }

  .intro-descr p {
    column-count: 1;
  }

  .glowing-card {
    padding: 30px 20px;
  }

  .card-content {
    flex-direction: column;
    gap: 30px;
  }

  .image-container {
    max-width: 100%;
    flex: none;
  }

  .chapter-header {
    flex-direction: column;
    gap: 5px;
    margin-bottom: 25px;
  }

  .card-title {
    font-size: 24px;
  }

  .chapter-divider {
    height: 40px;
    margin-top: 40px;
  }

  .image-container {
    max-width: 100%;
    width: 100%;
    flex: none;
  }

  .video-responsive {
    width: 100%;
    display: block;
  }
}
</style>
